import React, { useState, useEffect } from 'react';
import { X, Users, CheckCircle, Calendar, DollarSign, Building2, TrendingUp, PieChart as PieIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';

interface BranchReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    branch: any;
}

const BranchReportModal: React.FC<BranchReportModalProps> = ({ isOpen, onClose, branch }) => {
    const [stats, setStats] = useState({
        totalLeads: 0,
        enrolledStudents: 0,
        thisMonthEnrolled: 0,
        totalRevenue: 0,
        thisMonthRevenue: 0
    });
    const [chartData, setChartData] = useState<{
        monthlyData: any[];
        statusData: any[];
    }>({ monthlyData: [], statusData: [] });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (branch && isOpen) {
            fetchStats();
        }
    }, [branch, isOpen]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            // 1. Fetch ALL leads for this branch (Total Leads & Status Pie)
            const { data: leads, error: leadsError } = await supabase
                .from('leads')
                .select('id, status, is_registered, tracking_status')
                .eq('branch_id', branch.id)
                .limit(10000); // Increase limit to ensure accurate counts

            if (leadsError) throw leadsError;

            // 2. Fetch Accepted Proposals (For Revenue & Enrollment Timing)
            const { data: branchProposals, error: proposalError } = await supabase
                .from('proposals')
                .select('total_amount, created_at, currency, leads!inner(branch_id)')
                .eq('status', 'accepted')
                .eq('leads.branch_id', branch.id);

            if (proposalError) throw proposalError;

            const validProposals = branchProposals || [];
            const validLeads = leads || [];

            // -- Calculations --

            // Total Leads
            const totalLeads = validLeads.length;

            // Total Enrolled (From Leads status & Is Registered & Tracking Status)
            const enrolledCount = validLeads.filter(l =>
                l.status === 'enrolled' ||
                l.is_registered === true ||
                l.tracking_status === 'enrolled'
            ).length;

            // This Month Logic
            const now = new Date();
            const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

            // This Month Enrolled (Based on Proposal Date - verifying enrollment time)
            const thisMonthProposals = validProposals.filter(p =>
                new Date(p.created_at) >= firstDayOfMonth
            );
            const thisMonthEnrolledCount = thisMonthProposals.length;

            // Revenue Logic
            const parseAmount = (amount: any) => Number(amount) || 0;

            const totalRevenue = validProposals.reduce((sum, p) => sum + parseAmount(p.total_amount), 0);
            const thisMonthRevenue = thisMonthProposals.reduce((sum, p) => sum + parseAmount(p.total_amount), 0);

            setStats({
                totalLeads,
                enrolledStudents: enrolledCount,
                thisMonthEnrolled: thisMonthEnrolledCount,
                totalRevenue,
                thisMonthRevenue
            });

            // -- Charts --

            // 1. Pie Chart: Group into "Kayıt Oldu" vs "Yeni Lead"
            // "Kayıt Oldu" = status 'enrolled'
            // "Yeni Lead" = everything else
            const statusCounts = {
                'Kayıt Oldu': enrolledCount,
                'Yeni Lead': totalLeads - enrolledCount
            };

            const statusData = [
                { name: 'Kayıt Oldu', value: statusCounts['Kayıt Oldu'], color: '#10B981' }, // Green
                { name: 'Yeni Lead', value: statusCounts['Yeni Lead'], color: '#3B82F6' }   // Blue
            ].filter(item => item.value > 0);


            // 2. Bar Chart: Monthly Trends (from Proposals)
            const monthlyStats: Record<string, { enrolled: number, revenue: number }> = {};

            // Initialize last 6 months
            for (let i = 5; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const key = d.toLocaleString('tr-TR', { month: 'short' });
                monthlyStats[key] = { enrolled: 0, revenue: 0 };
            }

            validProposals.forEach(p => {
                const d = new Date(p.created_at);
                // Check if within last ~6 months
                if ((now.getTime() - d.getTime()) < 180 * 24 * 60 * 60 * 1000) {
                    const key = d.toLocaleString('tr-TR', { month: 'short' });
                    if (monthlyStats[key]) {
                        monthlyStats[key].enrolled += 1;
                        monthlyStats[key].revenue += parseAmount(p.total_amount);
                    }
                }
            });

            const monthlyData = Object.keys(monthlyStats).map(key => ({
                name: key,
                Öğrenci: monthlyStats[key].enrolled,
                Gelir: monthlyStats[key].revenue
            }));

            setChartData({ monthlyData, statusData });

        } catch (error) {
            console.error('Error fetching branch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !branch) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-5xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-800 my-8">

                {/* Header */}
                <div className="relative h-32 bg-gradient-to-r from-blue-600 to-purple-600">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="absolute -bottom-10 left-8 flex items-end space-x-4">
                        <div className="w-20 h-20 rounded-xl bg-white dark:bg-gray-800 p-1 shadow-lg flex items-center justify-center">
                            <Building2 className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="mb-3">
                            <h2 className="text-2xl font-bold text-white shadow-sm">
                                {branch.name}
                            </h2>
                            <p className="text-blue-100/90 font-medium text-sm">
                                {branch.city}, {branch.region} • Detaylı Performans Raporu
                            </p>
                        </div>
                    </div>
                </div>

                <div className="pt-14 p-8">

                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-900/30">
                            <div className="flex items-center space-x-2 mb-2">
                                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Toplam Lead</span>
                            </div>
                            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                {stats.totalLeads}
                            </p>
                        </div>

                        <div className="bg-green-50 dark:bg-green-900/10 p-4 rounded-xl border border-green-100 dark:border-green-900/30">
                            <div className="flex items-center space-x-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="text-sm font-medium text-green-900 dark:text-green-300">Kayıtlı Öğrenci</span>
                            </div>
                            <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                                {stats.enrolledStudents}
                            </p>
                        </div>

                        <div className="bg-purple-50 dark:bg-purple-900/10 p-4 rounded-xl border border-purple-100 dark:border-purple-900/30">
                            <div className="flex items-center space-x-2 mb-2">
                                <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                <span className="text-sm font-medium text-purple-900 dark:text-purple-300">Bu Ay Kayıt</span>
                            </div>
                            <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                                {stats.thisMonthEnrolled}
                            </p>
                        </div>

                        <div className="bg-yellow-50 dark:bg-yellow-900/10 p-4 rounded-xl border border-yellow-100 dark:border-yellow-900/30">
                            <div className="flex items-center space-x-2 mb-2">
                                <DollarSign className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                <span className="text-sm font-medium text-yellow-900 dark:text-yellow-300">Toplam Gelir (EUR)</span>
                            </div>
                            <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                                €{stats.totalRevenue.toLocaleString()}
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">
                                Bu Ay: €{stats.thisMonthRevenue.toLocaleString()}
                            </p>
                        </div>
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* Monthly Enrollment Trend */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                                <TrendingUp className="w-5 h-5 mr-2 text-gray-500" />
                                Kayıt Trendi (Son 6 Ay)
                            </h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData.monthlyData}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                        <XAxis dataKey="name" fontSize={12} stroke="#888888" />
                                        <YAxis fontSize={12} stroke="#888888" />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        />
                                        <Bar dataKey="Öğrenci" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Status Distribution */}
                        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                                <PieIcon className="w-5 h-5 mr-2 text-gray-500" />
                                Lead Durum Dağılımı
                            </h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={chartData.statusData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {chartData.statusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                    </div>

                </div>
            </div>
        </div>
    );
};

export default BranchReportModal;
